import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdminConfirmModal } from "./AdminConfirmModals";
import { ADMIN_TASK_STATUS_OPTIONS } from "./adminStatusUi";

/**
 * @param {{ id: string }[]} rows Visible table rows (filtered list).
 */
export function useAdminInboxSelection(rows) {
  const [selected, setSelected] = useState(() => new Set());
  const idKey = useMemo(() => rows.map((r) => r.id).join("\0"), [rows]);

  useEffect(() => {
    const allowed = new Set(rows.map((r) => r.id));
    setSelected((prev) => {
      const next = new Set();
      let pruned = false;
      for (const id of prev) {
        if (allowed.has(id)) next.add(id);
        else pruned = true;
      }
      if (!pruned && next.size === prev.size) return prev;
      return next;
    });
  }, [idKey, rows]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    const ids = rows.map((r) => r.id);
    setSelected((prev) => {
      const allIn = ids.length > 0 && ids.every((id) => prev.has(id));
      if (allIn) return new Set();
      return new Set(ids);
    });
  }, [rows]);

  const clear = useCallback(() => setSelected(new Set()), []);

  const selectedIds = useMemo(() => [...selected], [selected]);
  const selectedCount = selected.size;
  const visibleIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id));

  return useMemo(
    () => ({
      selected,
      selectedIds,
      selectedCount,
      toggle,
      toggleAllVisible,
      clear,
      allSelected,
      someSelected,
    }),
    [
      selected,
      selectedIds,
      selectedCount,
      toggle,
      toggleAllVisible,
      clear,
      allSelected,
      someSelected,
    ]
  );
}

export function AdminInboxBulkBar({
  selectedCount,
  onClear,
  onMarkRead,
  bulkStatus,
  onBulkStatusChange,
  onApplyStatus,
  onRequestDelete,
  busy,
  noun = "items",
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (selectedCount === 0) return null;

  return (
    <>
      <div
        className="admin-dash__bulk-bar"
        role="region"
        aria-label="Bulk actions for selected rows"
      >
        <p className="admin-dash__bulk-bar__summary">
          <strong>{selectedCount}</strong> {selectedCount === 1 ? noun.replace(/s$/, "") : noun}{" "}
          selected
        </p>
        <div className="admin-dash__bulk-bar__actions">
          <button
            type="button"
            className="admin-dash__btn-text admin-dash__bulk-bar__linkish"
            onClick={onClear}
            disabled={busy}
          >
            Clear selection
          </button>
          <button
            type="button"
            className="admin-dash__btn-secondary admin-dash__btn-secondary--compact"
            onClick={onMarkRead}
            disabled={busy}
          >
            Mark read
          </button>
          <div className="admin-dash__bulk-bar__status">
            <label className="admin-dash__bulk-bar__status-label">
              <span className="admin-dash__sr-only">Status for bulk update</span>
              <select
                className="admin-dash__input"
                value={bulkStatus}
                onChange={(e) => onBulkStatusChange(e.target.value)}
                disabled={busy}
              >
                {ADMIN_TASK_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="admin-dash__btn-primary admin-dash__btn-primary--compact"
              onClick={onApplyStatus}
              disabled={busy}
            >
              Set status
            </button>
          </div>
          <button
            type="button"
            className="admin-dash__btn-danger"
            onClick={() => setDeleteOpen(true)}
            disabled={busy}
          >
            Delete…
          </button>
        </div>
      </div>
      <AdminConfirmModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={`Delete ${selectedCount} ${selectedCount === 1 ? noun.replace(/s$/, "") : noun}?`}
        message="This removes the selected records from the inbox. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={onRequestDelete}
        danger
      />
    </>
  );
}

export function AdminInboxSelectAllHeader({
  allSelected,
  someSelected,
  onToggleAll,
  disabled,
}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.indeterminate = someSelected && !allSelected;
  }, [allSelected, someSelected]);

  return (
    <th scope="col" className="admin-dash__th-select">
      <input
        ref={ref}
        type="checkbox"
        className="admin-dash__checkbox"
        checked={allSelected && !disabled}
        onChange={onToggleAll}
        disabled={disabled}
        aria-label="Select all rows in this list"
      />
    </th>
  );
}
